import { Info, Loader2, RefreshCw } from 'lucide-react'
import { useMemo, useState } from 'react'

import { ConfirmDialog } from '@/components/forms'
import { Checkbox, Input } from '@/components/ui'
import type { ConnectorManagedRecord } from '../types'

export function ManagedRecordsTab({
  records,
  recordUnit,
  loading,
  failed,
  removing,
  syncing,
  resyncing,
  onSync,
  onResyncAll,
  onUnsync,
}: {
  records: ConnectorManagedRecord[]
  recordUnit: { singular: string; plural: string }
  loading: boolean
  failed: boolean
  removing: ReadonlySet<string>
  syncing: ReadonlySet<string>
  resyncing: boolean
  onSync: (records: ConnectorManagedRecord[]) => void
  onResyncAll: () => void
  onUnsync: (records: ConnectorManagedRecord[]) => void
}) {
  const [filter, setFilter] = useState('')
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set())
  const [pendingUnsync, setPendingUnsync] = useState<ConnectorManagedRecord[] | null>(null)
  const visible = useMemo(() => {
    const query = filter.trim().toLowerCase()
    return query
      ? records.filter(record => (
          `${record.identifier} ${record.title ?? ''}`.toLowerCase().includes(query)
        ))
      : records
  }, [filter, records])
  const isBusy = (record: ConnectorManagedRecord) =>
    removing.has(record.recordKey) || syncing.has(record.recordKey) || record.status === 'syncing'
  // cm-coding: Selection is a key set so it survives refetches, but the records
  // acted on are always re-derived from the current list — a row that finished
  // unsyncing or started a job elsewhere can never ride along in a bulk action.
  const selectable = visible.filter(record => !isBusy(record))
  const selectedRecords = selectable.filter(record => selected.has(record.recordKey))
  const allVisibleSelected = selectable.length > 0 && selectedRecords.length === selectable.length

  const toggleRecord = (recordKey: string, checked: boolean) => {
    setSelected(current => {
      const next = new Set(current)
      if (checked) next.add(recordKey)
      else next.delete(recordKey)
      return next
    })
  }

  const toggleAllVisible = () => {
    setSelected(allVisibleSelected
      ? new Set()
      : new Set(selectable.map(record => record.recordKey)))
  }

  const unsyncCount = pendingUnsync?.length ?? 0

  return (
    <div className="flex flex-col gap-2.5 overflow-hidden px-[22px] pb-4 pt-3.5">
      <div className="flex gap-2">
        <Input
          aria-label={`Filter synced ${recordUnit.plural}`}
          data-testid="managed-records-filter"
          placeholder={`Filter synced ${recordUnit.plural}…`}
          className="h-8 min-w-0 flex-1 rounded-lg px-[11px] text-[12.5px]"
          value={filter}
          onChange={event => setFilter(event.target.value)}
        />
        <button
          type="button"
          data-testid="managed-records-resync-all"
          className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-[11.5px] font-semibold text-slate-900 transition-colors hover:border-slate-300 disabled:cursor-wait disabled:text-slate-400"
          disabled={resyncing || records.length === 0}
          onClick={onResyncAll}
        >
          {resyncing
            ? <Loader2 className="size-3 animate-spin" />
            : <RefreshCw className="size-3" />}
          {resyncing ? 'Resyncing…' : 'Resync all'}
        </button>
      </div>
      <div data-testid="managed-records-list" className="flex max-h-[264px] flex-col overflow-y-auto rounded-[10px] border border-slate-100">
        {loading && (
          <p className="flex items-center justify-center gap-2 py-10 text-xs text-slate-500">
            <Loader2 className="size-3.5 animate-spin" /> Loading synced {recordUnit.plural}…
          </p>
        )}
        {!loading && failed && (
          <p className="py-10 text-center text-xs text-destructive">
            Synced {recordUnit.plural} could not be loaded.
          </p>
        )}
        {!loading && !failed && visible.length === 0 && (
          <p className="py-10 text-center text-xs text-slate-500">
            {records.length
              ? `No synced ${recordUnit.plural} match this filter.`
              : `No ${recordUnit.plural} are currently syncing.`}
          </p>
        )}
        {!loading && !failed && visible.length > 0 && (
          <div className="flex h-[38px] items-center gap-2.5 border-b border-slate-100 bg-slate-50/70 px-3">
            <Checkbox
              aria-label={`Select all ${recordUnit.plural}`}
              data-testid="managed-records-select-all"
              className="size-3.5"
              checked={selectedRecords.length === 0 ? false : allVisibleSelected ? true : 'indeterminate'}
              disabled={selectable.length === 0}
              onCheckedChange={toggleAllVisible}
            />
            {selectedRecords.length === 0 ? (
              <span className="text-[11px] font-medium text-slate-500">Select {recordUnit.plural} to sync or unsync together</span>
            ) : (
              <>
                <span data-testid="managed-records-selected-count" className="text-[11px] font-semibold text-slate-700">
                  {selectedRecords.length} selected
                </span>
                <div className="ml-auto flex gap-1.5">
                  <button
                    type="button"
                    data-testid="managed-records-sync-selected"
                    className="inline-flex h-[26px] items-center gap-1 rounded-[7px] border border-slate-200 bg-white px-[9px] text-[11px] font-semibold text-slate-600 transition-colors hover:border-slate-400 hover:text-slate-900"
                    onClick={() => {
                      onSync(selectedRecords)
                      setSelected(new Set())
                    }}
                  >
                    <RefreshCw className="size-2.5" />
                    Sync {selectedRecords.length}
                  </button>
                  <button
                    type="button"
                    data-testid="managed-records-unsync-selected"
                    className="h-[26px] rounded-[7px] border border-slate-200 bg-white px-[9px] text-[11px] font-semibold text-slate-600 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-700"
                    onClick={() => setPendingUnsync(selectedRecords)}
                  >
                    Unsync {selectedRecords.length}
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        {!loading && !failed && visible.map(record => {
          const removingRecord = removing.has(record.recordKey)
          const syncingRecord = syncing.has(record.recordKey) || record.status === 'syncing'
          return (
            <div
              key={record.recordKey}
              data-testid={`managed-record-${record.recordKey}`}
              className="flex items-center gap-2.5 border-b border-slate-100 bg-white px-3 py-[9px] last:border-b-0"
            >
              <Checkbox
                aria-label={`Select ${record.identifier}`}
                data-testid={`managed-record-select-${record.recordKey}`}
                className="size-3.5"
                checked={selected.has(record.recordKey) && !removingRecord && !syncingRecord}
                disabled={removingRecord || syncingRecord}
                onCheckedChange={checked => toggleRecord(record.recordKey, checked === true)}
              />
              <span data-testid={`managed-record-identifier-${record.recordKey}`} className="shrink-0 font-mono text-xs font-semibold text-slate-900">
                {record.identifier}
              </span>
              <span data-testid={`managed-record-title-${record.recordKey}`} className="min-w-0 flex-1 truncate text-xs text-slate-500">
                {record.title ?? ''}
              </span>
              <span data-testid={`managed-record-status-${record.recordKey}`} className={`shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${syncingRecord ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                {syncingRecord ? 'Syncing…' : 'Synced'}
              </span>
              <button
                type="button"
                title={`Re-import this ${recordUnit.singular} from the source now`}
                data-testid={`managed-record-sync-${record.recordKey}`}
                className="inline-flex h-[26px] shrink-0 items-center gap-1 rounded-[7px] border border-slate-200 bg-white px-[9px] text-[11px] font-semibold text-slate-600 transition-colors hover:border-slate-400 hover:text-slate-900 disabled:cursor-wait disabled:text-slate-400"
                disabled={syncingRecord}
                onClick={() => onSync([record])}
              >
                {syncingRecord
                  ? <Loader2 className="size-2.5 animate-spin" />
                  : <RefreshCw className="size-2.5" />}
                Sync
              </button>
              <button
                type="button"
                data-testid={`managed-record-unsync-${record.recordKey}`}
                className="h-[26px] shrink-0 rounded-[7px] border border-slate-200 bg-white px-[9px] text-[11px] font-semibold text-slate-600 transition-colors hover:border-red-300 hover:bg-red-50 hover:text-red-700 disabled:cursor-wait disabled:opacity-50"
                disabled={removingRecord}
                onClick={() => setPendingUnsync([record])}
              >
                {removingRecord ? 'Removing…' : 'Unsync'}
              </button>
            </div>
          )
        })}
      </div>
      <p className="flex items-start gap-1.5 text-[11.5px] leading-[1.5] text-slate-400">
        <Info className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
        <span>
          <strong className="font-semibold text-slate-500">Sync</strong> re-imports that {recordUnit.singular}&apos;s current source package now.{' '}
          <strong className="font-semibold text-slate-500">Unsync</strong> stops future syncs and removes its documents from the Vault — the source {recordUnit.singular} is untouched; you can add it back later.
        </span>
      </p>
      <ConfirmDialog
        open={pendingUnsync !== null}
        onOpenChange={open => { if (!open) setPendingUnsync(null) }}
        title={unsyncCount === 1
          ? `Unsync ${recordUnit.singular} ${pendingUnsync?.[0]?.identifier ?? ''}?`
          : `Unsync ${unsyncCount} ${recordUnit.plural}?`}
        description={unsyncCount === 1
          ? `This deletes every document synced from this ${recordUnit.singular} from the workspace and Vault. The source ${recordUnit.singular} is not changed, and you can add it again later.`
          : `This deletes every document synced from these ${unsyncCount} ${recordUnit.plural} from the workspace and Vault. The source ${recordUnit.plural} are not changed, and you can add them again later.`}
        confirmLabel={unsyncCount === 1
          ? `Unsync ${recordUnit.singular}`
          : `Unsync ${unsyncCount} ${recordUnit.plural}`}
        variant="destructive"
        testId="confirm-managed-record-unsync"
        onConfirm={() => {
          if (pendingUnsync) onUnsync(pendingUnsync)
          setSelected(new Set())
          setPendingUnsync(null)
        }}
      />
    </div>
  )
}
